import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, message, Modal } from 'antd';
import DeviceFilters from './DeviceFilters';
import DevicesTable from './DevicesTable';

import { listAllDevices, deleteDevice } from '../../../../api/deviceService';
import type { DeviceEntity } from '../../../../interface/responses/deviceResponse';
import type { DeviceListQueryWithFilters } from '../../../../api/deviceService';
import { ReloadOutlined } from '@ant-design/icons';

interface DeleteModalState {
  isOpen: boolean;
  device: DeviceEntity | null;
}

const DevicesTab: React.FC = () => {
  const navigate = useNavigate();

  // Backend query state
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<DeviceEntity[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  // Single search state - only one search field active at a time
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearchField, setActiveSearchField] = useState<'serial' | 'name' | 'location' | 'email' | 'phone' | ''>('');
  
  // Backend filtering and sorting
  const [groupFilter, setGroupFilter] = useState<number[]>([]);
  const [deviceTypeFilter, setDeviceTypeFilter] = useState<string[]>([]);
  const [sortField, setSortField] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');

  const [deleteModal, setDeleteModal] = useState<DeleteModalState>({
    isOpen: false,
    device: null,
  });

  // ✅ Simple fetch function
  const fetchData = async () => {
    setLoading(true);
    try {
      const query: DeviceListQueryWithFilters = {
        page,
        limit: pageSize,
      };
      
      if (sortField && sortOrder) {
        query.sortBy = sortField;
        query.order = sortOrder;
      }
      
      if (searchTerm.trim()) query.q = searchTerm.trim();
      if (groupFilter.length > 0) query.deviceGroupIds = groupFilter;
      if (deviceTypeFilter.length > 0) query.device_type = deviceTypeFilter[0] as "Mobile" | "STB";
      
      const response = await listAllDevices(query);
      setRows(response.data || []);
      setTotal(response.pagination?.totalRecords || 0);
    } catch (err: any) {
      console.error('Failed to fetch devices:', err);
      message.error('Failed to load devices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, pageSize, searchTerm, groupFilter, deviceTypeFilter, sortField, sortOrder]);

  // ✅ Simple pagination handler
  const handlePageChange = (newPage: number, newPageSize: number) => {
    
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
      setPage(1); // Reset to page 1 when page size changes
    } else {
      setPage(newPage);
    }
    // ✅ useEffect will handle the API call automatically
  };

  // ✅ Simple sort handler
  const handleRequestSort = (field: string, order: 'ASC' | 'DESC' | null) => {
    setSortField(field || '');
    setSortOrder(order);
    
    // Reset to page 1 when sorting changes
    if (page !== 1) {
      setPage(1);
    }
    // ✅ useEffect will handle the API call automatically
  };

  // ✅ Search handlers - simple state updates
  const handleSearchSerial = (value: string) => {
    setSearchTerm(value);
    setActiveSearchField('serial');
    if (page !== 1) setPage(1);
  };

  const handleSearchName = (value: string) => {
    setSearchTerm(value);
    setActiveSearchField('name');
    if (page !== 1) setPage(1);
  };

  const handleSearchLocation = (value: string) => {
    setSearchTerm(value);
    setActiveSearchField('location');
    if (page !== 1) setPage(1);
  };

  const handleSearchEmail = (value: string) => {
    setSearchTerm(value);
    setActiveSearchField('email');
    if (page !== 1) setPage(1);
  };

  const handleSearchPhone = (value: string) => {
    setSearchTerm(value);
    setActiveSearchField('phone');
    if (page !== 1) setPage(1);
  };

  const handleFilterGroups = (groupIds: React.Key[]) => {
    setGroupFilter(groupIds as number[]);
    if (page !== 1) setPage(1);
  };

  const handleFilterDeviceType = (deviceTypes: React.Key[]) => {
    setDeviceTypeFilter(deviceTypes as string[]);
    if (page !== 1) setPage(1);
  };

  const handleAddDevice = () => navigate('/device-management/add');

  const handleViewDevice = (device: DeviceEntity) => {
    navigate(`/device-management/view/${device.id}`, {
      state: { activeTab: 'devices' },
    });
  };

  const handleEditDevice = (device: DeviceEntity) => {
    navigate(`/device-management/edit/${device.id}`);
  };

  const handleDeleteDevice = (device: DeviceEntity) => {
    setDeleteModal({
      isOpen: true,
      device,
    });
  };

  const confirmDeleteDevice = async () => {
    if (!deleteModal.device) return;

    try {
      await deleteDevice(deleteModal.device.id);
      message.success('Device deleted successfully');
      setDeleteModal({ isOpen: false, device: null });
      fetchData(); // Refresh the list
    } catch (error) {
      console.error('Delete device error:', error);
      message.error('Failed to delete device');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button type="primary" onClick={handleAddDevice}>
          Add Device
        </Button>
        <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>
          Refresh
        </Button>
      </div>
      
      <DevicesTable
        loading={loading}
        data={rows}
        total={total}
        page={page}
        pageSize={pageSize}
        onChangePage={handlePageChange}
        serialSearch={activeSearchField === 'serial' ? searchTerm : ''}
        onSearchSerial={handleSearchSerial}
        nameSearch={activeSearchField === 'name' ? searchTerm : ''}
        onSearchName={handleSearchName}
        locationSearch={activeSearchField === 'location' ? searchTerm : ''}
        onSearchLocation={handleSearchLocation}
        emailSearch={activeSearchField === 'email' ? searchTerm : ''}
        onSearchEmail={handleSearchEmail}
        phoneSearch={activeSearchField === 'phone' ? searchTerm : ''}
        onSearchPhone={handleSearchPhone}
        groupFilteredValues={groupFilter}
        onFilterGroups={handleFilterGroups}
        deviceTypeFilteredValues={deviceTypeFilter}
        onFilterDeviceType={handleFilterDeviceType}
        onRequestSort={handleRequestSort}
        onViewDevice={handleViewDevice}
        onEditDevice={handleEditDevice}
        onDeleteDevice={handleDeleteDevice}
      />

      {/* Delete Device Modal */}
      <Modal
        title="Delete Device"
        open={deleteModal.isOpen}
        onOk={confirmDeleteDevice}
        onCancel={() => setDeleteModal({ isOpen: false, device: null })}
        okText="Delete"
        okType="danger"
      >
        <p>
          Are you sure you want to delete device{' '}
          <strong>{deleteModal.device?.device_serial || `#${deleteModal.device?.id}`}</strong>?
        </p>
        <p>This action cannot be undone.</p>
      </Modal>
    </div>
  );
};

export default DevicesTab;
